import { useState } from 'react';
import { toast } from 'react-toastify';
import moment from 'moment';
import request from '../../utils/APIHelper/request';
import { controllers, requestTypes } from '../../constants/enums/API';
import Table from '../../components/Table/Table';
import { exportToExcel } from '../../utils/Downloader/Downloader';

export default function DatePickerWithReport() {
  const [option, setOption] = useState('last7'); // last7 or custom
  const [startDate, setStartDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [shipments, setShipments] = useState([]);
  const [excelData, setExcelData] = useState([]);

  const handleOptionChange = (e) => setOption(e.target.value);
  const handleDateChange = (e) => setStartDate(e.target.value);

  const getSelectedStartDate = () => {
    if (option === 'last7') {
      const date = new Date();
      date.setDate(date.getDate() - 7);
      return date.toISOString().split('T')[0];
    } else if (option === 'custom' && startDate) {
      return startDate;
    }
    return null;
  };

  const getReport = async () => {
    const date = getSelectedStartDate();
    if (!date) {
      toast.error('Please select a date first!');
      return;
    }

    const comparePickupDate = (dateStr) => {
      const inputDate = new Date(dateStr);
      const today = new Date();

      const inputDateOnly = new Date(
        inputDate.getFullYear(),
        inputDate.getMonth(),
        inputDate.getDate(),
      );
      const todayOnly = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate(),
      );

      if (inputDateOnly < todayOnly) {
        return 0; // before today
      } else if (inputDateOnly > todayOnly) {
        return 1; // after today
      } else {
        return 2; // today
      }
    };

    try {
      setShipments([]);
      const result = await request({
        url: `${controllers.canpar}shipments-after`,
        type: requestTypes.get,
        params: { date },
        setLoading,
        disableLogOut: true,
      });

      if (result?.code !== 'success') {
        toast.error(result?.message || 'Failed to get report');
      } else {
        console.log(result?.result?.shipment);
        if (result?.result?.shipment?.length > 0) {
          const excelShipments = result?.result?.shipment?.map((item) => ({
            'Meest Tracking': item?.delivery_address?.attention,
            'CanPar Tracking': item?.packages?.[0]?.barcode,
            'Pickup Address': `${item?.pickup_address?.address_line_1}, ${item?.pickup_address?.city}, ${item?.pickup_address?.province} ${item?.pickup_address?.postal_code}`,
            Sender: item?.pickup_address?.name,
            "Sender's phone": item?.pickup_address?.phone,
            'Created At': moment(item?.inserted_on).format('MMM DD, YYYY'),
          }));
          setExcelData(excelShipments);
          const shipmentsCanPar = result?.result?.shipment?.map((item) => ({
            'Meest Tracking': item?.delivery_address?.attention,
            'CanPar Tracking': item?.packages?.[0]?.barcode,
            'Pickup Address': `${item?.pickup_address?.address_line_1}, ${item?.pickup_address?.city}, ${item?.pickup_address?.province} ${item?.pickup_address?.postal_code}`,
            Sender: item?.pickup_address?.name,
            "Sender's phone": item?.pickup_address?.phone,
            'Created At': (
              <div className="text-gray-400">
                {moment(item?.inserted_on).format('MMM DD, YYYY')}
              </div>
            ),
            'Scheduled Shipping Date': (
              <div
                className={
                  comparePickupDate(item?.shipping_date) === 0
                    ? 'text-red-600 font-medium'
                    : comparePickupDate(item?.shipping_date) === 1
                      ? 'text-green-600'
                      : 'text-yellow-600'
                }
              >
                {comparePickupDate(item?.shipping_date) === 0 && '!!! '}
                {moment(item?.shipping_date).format('MMM DD, YYYY')}
              </div>
            ),
          }));
          const results = shipmentsCanPar.filter(
            (item) => item?.['Meest Tracking'] !== '',
          );

          setShipments(results);
          toast.success('Report processed successfully!');
        } else {
          toast.info(
            `No unprocessed shipments were found ${option === 'last7' ? 'since a week ago' : 'before the selected date'}!`,
          );
        }
      }
    } catch (err) {
      toast.error('Error fetching report');
      console.error(err);
    }
  };

  const handleDownloadExcel = () => {
    if (!shipments.length) {
      toast.error('No shipments to download!');
      return;
    }
    exportToExcel(excelData, 'canpar-shipments-report');
  };

  return (
    <div className="mb-40">
      <div className="text-lg text-blue-800">CANPAR TRACKING</div>

      <div className="max-w-md mx-auto p-4 bg-white rounded-lg shadow-md mb-10">
        <h2 className="text-lg font-semilight mb-4 text-gray-400">
          Select Date Range
        </h2>

        <div className="flex flex-col gap-2 mb-4">
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="dateOption"
              value="last7"
              checked={option === 'last7'}
              onChange={handleOptionChange}
              className="accent-blue-500"
            />
            Since a week ago
          </label>

          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="dateOption"
              value="custom"
              checked={option === 'custom'}
              onChange={handleOptionChange}
              className="accent-blue-500"
            />
            Customize Start Date
          </label>

          {option === 'custom' && (
            <input
              type="date"
              value={startDate}
              onChange={handleDateChange}
              className="mt-2 p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          )}
        </div>

        <button
          onClick={getReport}
          disabled={loading}
          className={`w-full py-2 px-4 rounded-full bg-blue-900 text-white font-semilight hover:bg-blue-800 transition ${
            loading ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          {loading ? 'Processing...' : 'Get Report'}
        </button>
      </div>

      {shipments.length > 0 && (
        <div className="mx-2 my-2">
          <div className="float-left">
            Shipments number: {shipments?.length}
          </div>
          <button
            onClick={handleDownloadExcel}
            className="px-5 border bg-blue-100 border-blue-700 text-blue-700 rounded-md transition inline-block float-right"
          >
            Download Excel
          </button>
        </div>
      )}
      <br></br>
      <br></br>
      <div>
        <Table
          data={shipments}
          rowsClassName="!text-center border-b border-blue-300"
          headersClassNames="!text-center bg-blue-100"
          containerClassName="text-[0.9rem] whitespace-nowrap text-center"
        />
      </div>
    </div>
  );
}
